import { ArrowLeft, ArrowRight, CalendarDays, Check, Printer, ShieldCheck } from "lucide-react";
import { Notice } from "../../components/Notice";
import { catalogById } from "../../data/catalog";
import type { BalconyProfile, DesignItem } from "../../data/model";
import { servicePackages } from "../../data/services";
import { FIXED_REFERENCE_DATE, careLabels, lightLabels, styles } from "../../data/taxonomy";
import { areaM2, careMinutes, estimateLines, estimateTotal, plantCount, wateringSessions } from "../../lib/calculations";
import { formatArea, formatMoney } from "../../lib/format";

interface Props {
  profile: BalconyProfile;
  design: DesignItem[];
  packageId: string;
  onBack: () => void;
  onConsult: () => void;
}

const categoryLabels = {
  plant: "Cây",
  pot: "Chậu",
  furniture: "Nội thất",
  accessory: "Phụ kiện",
};

export function EstimateReview({ profile, design, packageId, onBack, onConsult }: Props) {
  const lines = estimateLines(design);
  const service = servicePackages.find((entry) => entry.id === packageId)!;
  const total = estimateTotal(design, packageId);
  const delta = profile.budget - total;
  const groups = (Object.keys(categoryLabels) as Array<keyof typeof categoryLabels>).map((type) => ({
    type,
    label: categoryLabels[type],
    lines: lines.filter((line) => line.type === type),
  })).filter((group) => group.lines.length);

  const plantNames = design
    .filter((item) => item.type === "plant")
    .map((item) => catalogById.get(item.refId)?.name)
    .filter(Boolean)
    .slice(0, 3)
    .join(", ");

  return (
    <div className="review-page">
      <div className="review-top">
        <button className="back-link" type="button" onClick={onBack}><ArrowLeft aria-hidden="true" /> Quay lại workspace</button>
        <div><span className="eyebrow">Bước 3/4 · kiểm tra</span><h1>Một con số có thể lần theo.</h1><p>Mỗi dòng dưới đây lấy trực tiếp từ bản phác đang mở.</p></div>
        <button className="button button-ghost print-button" type="button" onClick={() => window.print()}><Printer aria-hidden="true" /> In bản tóm tắt</button>
      </div>

      <div className="review-layout">
        <section className="estimate-sheet" aria-labelledby="estimate-title">
          <div className="estimate-profile">
            <div><span className="eyebrow">Rêu Nắng · dự toán mô phỏng</span><h2 id="estimate-title">{styles.find((style) => style.id === profile.styleId)?.shortName}</h2></div>
            <dl>
              <div><dt>Kích thước</dt><dd>{profile.widthCm} × {profile.depthCm} cm</dd></div>
              <div><dt>Diện tích</dt><dd>{formatArea(areaM2(profile.widthCm, profile.depthCm))} m²</dd></div>
              <div><dt>Ánh sáng</dt><dd>{lightLabels[profile.light]}</dd></div>
              <div><dt>Chăm</dt><dd>{careLabels[profile.care]}</dd></div>
            </dl>
          </div>
          {groups.map((group) => (
            <div className="estimate-group" key={group.type}>
              <h3>{group.label}</h3>
              <div className="estimate-table" role="table" aria-label={`Chi phí ${group.label.toLowerCase()}`}>
                {group.lines.map((line) => (
                  <div className="estimate-row" role="row" key={line.refId}>
                    <span role="cell"><strong>{line.name}</strong><small>{formatMoney(line.unitPrice)} × {line.qty}</small></span>
                    <b role="cell">{formatMoney(line.total)}</b>
                  </div>
                ))}
                <div className="estimate-subtotal">
                  <span>Cộng {group.label.toLowerCase()}</span>
                  <strong>{formatMoney(group.lines.reduce((sum, line) => sum + line.total, 0))}</strong>
                </div>
              </div>
            </div>
          ))}
          <div className="estimate-group">
            <h3>Dịch vụ</h3>
            <div className="estimate-row service-row">
              <span><strong>Gói {service.name}</strong><small>{service.leadTime}</small></span>
              <b>{formatMoney(service.price)}</b>
            </div>
          </div>
          <div className="estimate-total">
            <span><small>Tổng dự toán mô phỏng</small><strong>{formatMoney(total)}</strong></span>
            <span className={delta < 0 ? "over" : ""}>{delta < 0 ? `Vượt ${formatMoney(Math.abs(delta))}` : `Còn ${formatMoney(delta)}`}</span>
          </div>
          <p className="estimate-disclaimer">Chưa gồm chống thấm, điện nước, gia cố, vận chuyển đặc thù hoặc phí quản lý tòa nhà.</p>
        </section>

        <aside className="care-sheet">
          <span className="eyebrow">Lịch bắt đầu từ 03/08/2026</span>
          <h2>Tuần đầu, từng việc một.</h2>
          <p>{plantCount(design)} cây · khoảng {careMinutes(design)} phút/tuần · tối đa {wateringSessions(design)} phiên tưới.</p>
          <div className="week-list">
            {[
              ["T2 · 03/08", "Kiểm tra ẩm", `Chạm giá thể ${plantNames || "từng chậu"} trước khi tưới.`],
              ["T4 · 05/08", "Tưới theo nhóm", "Tưới chậm đến khi có nước thoát, đổ khay hứng."],
              ["T6 · 07/08", "Lau & xoay chậu", "Lau lá bản lớn, xoay nhóm cây vào sáng."],
              ["CN · 09/08", "Tỉa nhẹ", "Bỏ lá già và ghi lại chậu khô nhanh hơn."],
            ].map(([date, title, body], index) => (
              <article key={date}>
                <span>{index + 1}</span>
                <div><small>{date}</small><h3>{title}</h3><p>{body}</p></div>
              </article>
            ))}
          </div>
          <div className="routine-card">
            <CalendarDays aria-hidden="true" />
            <div><strong>Nhịp dài hơn</strong><p>Mỗi tháng bón dinh dưỡng nhẹ; mỗi quý kiểm tra rễ, dây buộc và lỗ thoát.</p></div>
          </div>
          <Notice tone="info" title={`Mốc dữ liệu cố định: ${FIXED_REFERENCE_DATE}.`}>
            <p>Lịch không tự đổi theo ngày mở demo để mọi phép đối soát luôn giống nhau.</p>
          </Notice>
        </aside>
      </div>

      <section className="review-safety">
        <div><ShieldCheck aria-hidden="true" /><span><strong>Trước ngày lắp</strong> 4 việc cần người thật xác nhận.</span></div>
        <ul>
          {["Tải trọng và vị trí chịu lực", "Chống thấm & lỗ thoát", "Nội quy tòa nhà", "Ổ điện ngoài trời"].map((item) => <li key={item}><Check aria-hidden="true" /> {item}</li>)}
        </ul>
      </section>

      <div className="review-actions">
        <button className="button button-ghost" type="button" onClick={onBack}><ArrowLeft aria-hidden="true" /> Chỉnh phương án</button>
        <button className="button button-primary button-large" type="button" onClick={onConsult}>Gửi yêu cầu tư vấn mô phỏng <ArrowRight aria-hidden="true" /></button>
      </div>
    </div>
  );
}
